import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import Select from 'react-select'; // Import react-select
import CircularProgress from '@mui/material/CircularProgress';

function IncExpSummary() {
    const [isLoading, setIsLoading] = useState(false);
    const [dataBill, setDataBill] = useState([]);
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const tableRef = React.createRef();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedOption, setSelectedOption] = useState('itemWise');
    const [customerOptions, setCustomerOptions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerId, setCustomer] = useState('0');
    const { isAuthenticated, companyId, permissions, token, logout } = useAuth();
    const [totalIncomeAmt, setTotalIncomeAmt] = useState(0);
    const [totalExpenseAmt, setTotalExpenseAmt] = useState(0);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
        fetchCustomerData();
    }, [isAuthenticated, companyId, token, logout]);

    useEffect(() => {
        // Calculate total income and expense amounts whenever dataItem changes //
        let incomeTotal = 0;
        let expenseTotal = 0;
        dataItem.forEach(item => {
            incomeTotal += item.IncomeAmt;
            expenseTotal += item.ExpenseAmt;
        });
        setTotalIncomeAmt(incomeTotal);
        setTotalExpenseAmt(expenseTotal);
    }, [dataItem]);

    const fetchCustomerData = () => {
        fetchData('ReportApi', `/Party/${companyId}`, token)
            .then((response) => response.response_data)
            .then((data) => {
                const options = data.map((customer) => ({
                    label: customer.Description,
                    value: customer.Id,
                }));
                setCustomerOptions(options);
            })
            .catch((error) => {
                console.error('Error fetching customer data:', error);
            });
    };




    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };


    const hasReadReportPermission = permissions.includes('Read Reports');

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            setDataItem([]);
            try {

                const response = await fetchData('ReportApi', `/IncExp/${companyId}/${fromDate}/${toDate}`, token);

                if (response && response.responseCode === '1111') {
                    setDataItem(response.response_data);
                } else {
                    console.error('Unexpected response:', response);
                }
            } catch (error) {
                console.error('Error fetching Packing Slip Details:', error);
            }
        }
    };

    // Usage example
    // Ensure to call fetchTableData after setting the dates
    useEffect(() => {
        if (fromDate && toDate) {
            fetchTableData();
        }
    }, [fromDate, toDate, hasReadReportPermission]);

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handleExportExcel = () => {
        const excelData = [];
        const headerCaption = [`Income Expense Summary From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`];
        excelData.push(headerCaption);
        const headerRow = ['Date', 'Description', 'Credit', 'Debit'];
        excelData.push(headerRow);
        filteredDataItem.forEach((rowData, index) => {
            const dataRow = [];
            dataRow.push(formatDate(rowData.RefDate), rowData.Description1, rowData.IncomeAmt, rowData.ExpenseAmt);
            excelData.push(dataRow);
        });
        // Add total row
        const totalRow = ['', 'Total', totalIncomeAmt.toFixed(2), totalExpenseAmt.toFixed(2)];
        excelData.push(totalRow);
        const ws = XLSX.utils.aoa_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Income Expense Summary');
        const wsCols = [
            { wch: 15 }, // Date
            { wch: 35 }, // Description
            { wch: 15 }, // Credit
            { wch: 15 }, // Debit          
        ];
        ws['!cols'] = wsCols;
        XLSX.writeFile(wb, 'IncomeExpense.xlsx');
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            console.error('Failed to open print window.');
            return;
        }
        printWindow.document.write('<html><head><title>Ledger</title></head><body>');
        const title = `Income Expense Summary`;
        printWindow.document.write('<html><head><title>' + title + '</title></head><body>');
        printWindow.document.write(`<h4 style="font-family: Times New Roman; font-size: 22px;">From : ${formatDate(fromDate)} To : ${formatDate(toDate)}</h4>`);
        printWindow.document.write('<table style="border-collapse: collapse; width: 100%;">');
        printWindow.document.write('<thead style="background-color: #f3f3f3;">');
        printWindow.document.write('<tr>');
        printWindow.document.write('<th style="border: 1px solid #ddd; font-size: 19px; font-weight: bolder; color: black;">Date</th>');
        printWindow.document.write('<th style="border: 1px solid #ddd; font-size: 19px; font-weight: bolder; color: black;">Description</th>');
        printWindow.document.write('<th style="border: 1px solid #ddd; font-size: 19px; font-weight: bolder; color: black;">Credit</th>');
        printWindow.document.write('<th style="border: 1px solid #ddd; font-size: 19px; font-weight: bolder; color: black;">Debit</th>');
        printWindow.document.write('</tr>');
        printWindow.document.write('</thead>');
        printWindow.document.write('<tbody>');
        filteredDataItem.forEach((rowData, index) => {
            printWindow.document.write('<tr>');
            printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${formatDate(rowData.RefDate)}</td>`);
            printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${rowData.Description1}</td>`);
            printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${rowData.IncomeAmt}</td>`);
            printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px;">${rowData.ExpenseAmt}</td>`);
            printWindow.document.write('</tr>');
        });
        // Print total row
        printWindow.document.write('<tr>');
        printWindow.document.write('<td style="border: 1px solid #ddd; font-size: 16px;"></td>');
        printWindow.document.write('<td style="border: 1px solid #ddd; font-size: 16px;">Total</td>');
        printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px; textAlign:right;">${totalIncomeAmt.toFixed(2)}</td>`);
        printWindow.document.write(`<td style="border: 1px solid #ddd; font-size: 16px; textAlign:right;">${totalExpenseAmt.toFixed(2)}</td>`);
        printWindow.document.write('</tr>');
        printWindow.document.write('</tbody>');
        printWindow.document.write('</table>');
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <div className="w-full  mx-auto" style={{ width: '95%', marginTop: '10px' }}>
            <div style={{ width: '100%', marginBottom: '2', display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                <div className='w-full mb-2 flex' style={{ marginTop: '10px' }}>
                    <h6 className="block uppercase tracking-wide text-black productHead flex-grow linkcolor" style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif', color: 'black', fontWeight: 'bolder' }}>
                        INCOME EXPENSE SUMMARY
                    </h6>
                </div>

                <div style={{ marginBottom: '10px', marginLeft: '-20px', display: 'flex', alignItems: 'center' }}>
                    <SearchIcon onClick={toggleSearchInput} style={{ cursor: 'pointer' }} />
                    {showSearchInput && (
                        <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearch}
                            style={{ marginLeft: '10px', width: '200px' }}
                        />
                    )}
                </div>

                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        From Date
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={fromDate} onChange={handleFromDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        To Date
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={toDate} onChange={handleToDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <Button onClick={handleExportExcel} style={{ background: '#dc3545', color: 'whitesmoke', fontWeight: 'bolder', height: '25px', marginLeft: '5px', marginTop: '25px' }} disabled={isLoading}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : "Excel"}
                </Button>
            </div>
            <div className='w-full mb-5 flex' style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>

                        <TableHead sx={{ backgroundColor: '#090040', Width: '2500px' }}>
                            <TableRow>
                                <TableCell sx={{ backgroundColor: '#090040', borderBottom: '1px solid #ddd', fontSize: '12px', fontWeight: 'bolder', color: 'White', padding: '7px', textTransform: 'uppercase' }}>Date</TableCell>
                                <TableCell sx={{ backgroundColor: '#090040', borderBottom: '1px solid #ddd', fontSize: '12px', fontWeight: 'bolder', color: 'White', padding: '7px', textTransform: 'uppercase' }}>Description</TableCell>
                                <TableCell sx={{ backgroundColor: '#090040', borderBottom: '1px solid #ddd', fontSize: '12px', fontWeight: 'bolder', color: 'White', textAlign: 'right', padding: '7px', textTransform: 'uppercase' }}>Income</TableCell>
                                <TableCell sx={{ backgroundColor: '#090040', borderBottom: '1px solid #ddd', fontSize: '12px', fontWeight: 'bolder', color: 'White', textAlign: 'right', padding: '7px', textTransform: 'uppercase' }}>Expense</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredDataItem.map((data, index) => (
                                <TableRow key={data.id}>
                                    <TableCell sx={{ borderBottom: '1px solid #17202A', fontSize: '13px', height: '30px', padding: '11px' }}>{formatDate(data.RefDate)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #17202A', fontSize: '13px', height: '30px', padding: '11px', fontWeight: 'normal' }}>{data.Description1}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #17202A', fontSize: '13px', height: '30px', padding: '11px', textAlign: 'right', fontWeight: 'normal' }}>{data.IncomeAmt.toFixed(2)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #17202A', fontSize: '13px', height: '30px', padding: '11px', textAlign: 'right', fontWeight: 'normal' }}>{data.ExpenseAmt.toFixed(2)}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell colSpan={2} />
                                <TableCell sx={{ borderBottom: '1px solid #17202A', fontWeight: 'bold', textAlign: 'right' }}>Total Income: {totalIncomeAmt.toFixed(2)}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #17202A', fontWeight: 'bold', textAlign: 'right' }}>Total Expense: {totalExpenseAmt.toFixed(2)}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );
}

export default IncExpSummary;
